import React, { useState } from "react";
import { Box, TextField, Button, Typography } from "@mui/material";
import { collection, addDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { db, storage } from "../../../firestore-DB-config/firebase-config"; // Assuming storage is configured here
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";

const AddBlog: React.FC = () => {
  const [formData, setFormData] = useState({
    title: "",
    cardDescription: "",
    description: "",
    author: "",
    datePublished: "",
    blogImage: null as File | null,
    authorImage: null as File | null,
    slug: "",
  });

  const createSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/ /g, "-") // Replace spaces with hyphens
      .replace(/[^\w-]+/g, ""); // Remove special characters
  };

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState({
    blogImage: 0,
    authorImage: 0,
  });
  const [imagePreviews, setImagePreviews] = useState({
    blogImagePreview: "",
    authorImagePreview: "",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.target;

    if (files) {
      const file = files[0];
      setFormData((prevData) => ({
        ...prevData,
        [name]: file,
      }));

      const reader = new FileReader();
      reader.onload = () => {
        setImagePreviews((prevPreviews) => ({
          ...prevPreviews,
          [`${name}Preview`]: reader.result as string,
        }));
      };
      reader.readAsDataURL(file);
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
        slug: name === "title" ? createSlug(value) : prevData.slug, // Update slug based on title change
      }));
    }
  };

  const uploadImage = async (file: File, path: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const storageRef = ref(storage, `${path}/${Date.now()}_${file.name}`); // Adding unique file name
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress((prevProgress) => ({
            ...prevProgress,
            [path]: progress,
          }));
        },
        (error) => reject(error),
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
        }
      );
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Upload blog image
      const blogImageUrl = formData.blogImage
        ? await uploadImage(formData.blogImage, "blogImages")
        : "";

      // Upload author image
      const authorImageUrl = formData.authorImage
        ? await uploadImage(formData.authorImage, "authorImages")
        : "";

      // Format the description
      const formattedDescription = formData.description
        .split("\n")
        .map((para) => `<p>${para}</p>`)
        .join("");

      // Add blog data to Firestore
      const blogData = {
        title: formData.title,
        cardDescription: formData.cardDescription,
        description: formattedDescription,
        author: formData.author,
        datePublished: formData.datePublished,
        blogImageUrl,
        authorImageUrl,
        slug: formData.slug, // Add slug here
      };

      await addDoc(collection(db, "blogs"), blogData);
      console.log("Blog submitted successfully!");

      // Reset the form
      resetForm();
    } catch (err) {
      setError("Failed to submit blog. Please try again.");
      console.error("Error submitting blog: ", err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      cardDescription: "",
      description: "",
      author: "",
      datePublished: "",
      blogImage: null,
      authorImage: null,
      slug: "",
    });
    setImagePreviews({
      blogImagePreview: "",
      authorImagePreview: "",
    });
    setUploadProgress({ blogImage: 0, authorImage: 0 });
  };

  return (
    <>
      <Header />
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          maxWidth: 600,
          margin: "auto",
          my: 4,
          p: 3,
          boxShadow: 2,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" mb={2}>
          Submit Your Blog
        </Typography>
        <TextField
          fullWidth
          label="Title"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
          required
        />
        <TextField
          fullWidth
          label="Card Description"
          name="cardDescription"
          value={formData.cardDescription}
          onChange={handleInputChange}
          multiline
          rows={2}
          sx={{ mb: 2 }}
          required
        />
        <TextField
          fullWidth
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          multiline
          rows={4}
          sx={{ mb: 2 }}
          required
        />
        <TextField
          label="Date Published"
          name="datePublished"
          type="date"
          value={formData.datePublished}
          onChange={handleInputChange}
          fullWidth
          required
          sx={{ mb: 2 }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          fullWidth
          label="Author"
          name="author"
          value={formData.author}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
          required
        />

        {/* Blog Image Input with Preview */}
        <Typography variant="body1" sx={{ mb: 2 }}>
          Blog Image
        </Typography>
        <input type="file" name="blogImage" onChange={handleInputChange} />
        {imagePreviews.blogImagePreview && (
          <Box mt={2}>
            <img
              src={imagePreviews.blogImagePreview}
              alt="Blog Preview"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </Box>
        )}
        {uploadProgress.blogImage > 0 && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            Uploading Blog Image: {uploadProgress.blogImage.toFixed(2)}%
          </Typography>
        )}

        {/* Author Image Input with Preview */}
        <Typography variant="body1" sx={{ mt: 4, mb: 2 }}>
          Author Image
        </Typography>
        <input type="file" name="authorImage" onChange={handleInputChange} />
        {imagePreviews.authorImagePreview && (
          <Box mt={2}>
            <img
              src={imagePreviews.authorImagePreview}
              alt="Author Preview"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </Box>
        )}
        {uploadProgress.authorImage > 0 && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            Uploading Author Image: {uploadProgress.authorImage.toFixed(2)}%
          </Typography>
        )}

        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        <Button
          variant="contained"
          type="submit"
          disabled={loading}
          sx={{ width: "100%", mt: "25px" }}
        >
          {loading ? "Submitting..." : "Submit Blog"}
        </Button>
      </Box>
      <Footer />
    </>
  );
};

export default AddBlog;
